import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getStoredUserId } from "../lib/authStorage";

const CartContext = createContext(null);
const getOwner = () => getStoredUserId() || "guest";
const getCartKey = (owner) => `dealswapify_cart_${owner}`;

const readCart = (owner) => {
  try {
    const stored = JSON.parse(
      localStorage.getItem(getCartKey(owner)) || "[]"
    );
    return Array.isArray(stored) ? stored : [];
  } catch {
    localStorage.removeItem(getCartKey(owner));
    return [];
  }
};

const buildCartItem = (item) => ({
  itemId: item.itemId,
  itemName: item.itemName,
  description: item.description,
  imageUrl: item.imageUrl,
  amount: item.amount,
  currency: item.currency,
  categoryName: item.categoryName,
  type: item.type,
  status: item.status,
  locCountry: item.locCountry || item.country || "",
  locCity: item.locCity || item.city || "",
});

export const CartProvider = ({ children }) => {
  const [owner, setOwner] = useState(getOwner);
  const [items, setItems] = useState(() => readCart(getOwner()));

  const persist = useCallback(
    (nextItems, targetOwner = owner) => {
      localStorage.setItem(
        getCartKey(targetOwner),
        JSON.stringify(nextItems)
      );
    },
    [owner]
  );

  useEffect(() => {
    const syncAuth = () => {
      const nextOwner = getOwner();

      if (owner === "guest" && nextOwner !== "guest") {
        const guestItems = readCart("guest");
        const accountItems = readCart(nextOwner);
        const merged = [...accountItems];

        guestItems.forEach((item) => {
          if (!merged.some((entry) => entry.itemId === item.itemId)) {
            merged.push(item);
          }
        });

        localStorage.setItem(getCartKey(nextOwner), JSON.stringify(merged));
        localStorage.removeItem(getCartKey("guest"));
        setOwner(nextOwner);
        setItems(merged);
        return;
      }

      setOwner(nextOwner);
      setItems(readCart(nextOwner));
    };

    const syncStorage = (event) => {
      if (event.key === getCartKey(owner)) {
        setItems(readCart(owner));
      }
    };

    window.addEventListener("dealswapify:auth-changed", syncAuth);
    window.addEventListener("storage", syncStorage);

    return () => {
      window.removeEventListener("dealswapify:auth-changed", syncAuth);
      window.removeEventListener("storage", syncStorage);
    };
  }, [owner]);

  const addToCart = useCallback(
    (item) => {
      if (
        !item?.itemId ||
        items.some((entry) => entry.itemId === item.itemId)
      ) {
        return false;
      }

      const next = [buildCartItem(item), ...items];
      setItems(next);
      persist(next);
      return true;
    },
    [items, persist]
  );

  const removeFromCart = useCallback(
    (itemId) => {
      const next = items.filter((item) => item.itemId !== itemId);
      setItems(next);
      persist(next);
    },
    [items, persist]
  );

  const toggleCartItem = useCallback(
    (item) => {
      const exists = items.some((entry) => entry.itemId === item.itemId);
      if (exists) {
        removeFromCart(item.itemId);
        return false;
      }

      addToCart(item);
      return true;
    },
    [items, addToCart, removeFromCart]
  );

  const clearCart = useCallback(() => {
    setItems([]);
    persist([]);
  }, [persist]);

  const isInCart = useCallback(
    (itemId) => items.some((item) => item.itemId === itemId),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      count: items.length,
      addToCart,
      removeFromCart,
      toggleCartItem,
      clearCart,
      isInCart,
    }),
    [
      items,
      addToCart,
      removeFromCart,
      toggleCartItem,
      clearCart,
      isInCart,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return context;
};
